import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { formatError } from '../../utils/errorUtils';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Search,
    Filter,
    Eye,
    CheckCircle,
    XCircle,
    Building2,
    MapPin,
    ExternalLink,
    Shield,
    Star,
    Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProperties() {
    const { getAuthHeader } = useAuth();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all, pending_rera
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchProperties();
    }, [page, search, filter]);

    const fetchProperties = async () => {
        setLoading(true);
        try {
            // Note: We might need a dedicated admin search endpoint, 
            // but for now we'll use the public search with admin privileges or a new endpoint if needed.
            // Actually, let's assume we use the existing public search but we might need to filter by 'rera_pending'
            // If the backend doesn't support 'rera_pending' filter yet, we might need to add it or do client side.
            // Let's use a new endpoint or the reports one? 
            // Ideally we should have an admin list properties endpoint.
            // For now, let's try to use the public list but maybe we need to add 'rera_verified' to filters?
            // Or better, let's just fetch recent properties and filter client side for MVP or add a query param.

            // WAIT: The user said "search the property in website". 
            // Let's implement a verified admin endpoint for properties if possible, 
            // or use the existing `search_all_properties` and just add a flag?
            // Let's assume `GET /api/public/properties` works but we want to see ALL statuses.

            // Let's add a `rera_pending=true` param to the existing search or a new admin endpoint.
            // Checking server.py, `search_all_properties` filters `is_active=True`.
            // We might want to see banned ones too?
            // Let's add `GET /api/admin/properties` separately to be safe.

            const params = {
                page,
                limit: 20,
                search,
            };

            if (filter === 'pending_rera') {
                params.rera_pending = true;
            }

            const response = await axios.get(`${API_URL}/api/admin/properties`, {
                headers: getAuthHeader(),
                params
            });

            setProperties(response.data.properties);
            setTotalPages(Math.ceil(response.data.total / 20));

        } catch (error) {
            const message = formatError(error.response?.data?.detail) || 'Failed to fetch properties';
            toast.error(message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleApproveRera = async (propertyId) => {
        try {
            await axios.put(
                `${API_URL}/api/admin/properties/${propertyId}/approve-rera`,
                {},
                { headers: getAuthHeader() }
            );
            toast.success('RERA ID Verified Successfully');
            fetchProperties();
        } catch (error) {
            const message = formatError(error.response?.data?.detail) || 'Verification failed';
            toast.error(message);
        }
    };

    const handleToggleVerify = async (propertyId) => {
        try {
            const response = await axios.put(`${API_URL}/api/admin/properties/${propertyId}/verify`, {}, { headers: getAuthHeader() });
            toast.success(response.data.message);
            setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, is_verified: response.data.is_verified } : p));
        } catch (error) {
            toast.error('Failed to toggle verification');
        }
    };

    const handleToggleSponsor = async (propertyId) => {
        try {
            const response = await axios.put(`${API_URL}/api/admin/properties/${propertyId}/sponsor`, {}, { headers: getAuthHeader() });
            toast.success(response.data.message);
            setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, is_sponsored: response.data.is_sponsored } : p));
        } catch (error) {
            toast.error('Failed to toggle sponsorship');
        }
    };

    return (
        <div className="p-6 lg:p-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-navy-900">Properties Management</h1>
                    <p className="text-navy-600">View and manage all properties</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant={filter === 'all' ? 'default' : 'outline'}
                        onClick={() => setFilter('all')}
                    >
                        All Properties
                    </Button>
                    <Button
                        variant={filter === 'pending_rera' ? 'default' : 'outline'}
                        onClick={() => setFilter('pending_rera')}
                        className="gap-2"
                    >
                        <Shield className="w-4 h-4" />
                        Pending RERA
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-navy-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-navy-100 flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                            placeholder="Search by title, agency, or RERA ID..."
                            className="pl-10"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Property</TableHead>
                            <TableHead>Agency</TableHead>
                            <TableHead>RERA Status</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><div className="h-4 bg-navy-100 rounded w-48 animate-pulse" /></TableCell>
                                    <TableCell><div className="h-4 bg-navy-100 rounded w-32 animate-pulse" /></TableCell>
                                    <TableCell><div className="h-4 bg-navy-100 rounded w-24 animate-pulse" /></TableCell>
                                    <TableCell><div className="h-4 bg-navy-100 rounded w-16 animate-pulse" /></TableCell>
                                    <TableCell><div className="h-8 bg-navy-100 rounded w-20 animate-pulse" /></TableCell>
                                </TableRow>
                            ))
                        ) : properties.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-8 text-navy-500">
                                    No properties found
                                </TableCell>
                            </TableRow>
                        ) : (
                            properties.map((prop) => (
                                <TableRow key={prop.id}>
                                    <TableCell>
                                        <div>
                                            <p className="font-medium text-navy-900">{prop.title}</p>
                                            <p className="text-sm text-navy-500 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {prop.location_city}
                                            </p>
                                            <div className="flex gap-1 mt-1">
                                                {prop.is_verified && (
                                                    <Badge className="bg-sky-500 text-[10px] h-4 px-1">
                                                        <Shield className="w-2 h-2 mr-1" /> VERIFIED
                                                    </Badge>
                                                )}
                                                {prop.is_sponsored && (
                                                    <Badge className="bg-amber-500 text-[10px] h-4 px-1">
                                                        <Star className="w-2 h-2 mr-1" /> SPONSORED
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-navy-900">{prop.agency_slug}</span>
                                    </TableCell>
                                    <TableCell>
                                        {prop.rera_number ? (
                                            <div className="flex flex-col gap-1">
                                                <span className="font-mono text-xs">{prop.rera_number}</span>
                                                {prop.rera_verified ? (
                                                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 w-fit">
                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                        Verified
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 w-fit">
                                                        Pending
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-navy-400 text-sm">-</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={prop.is_active ? 'default' : 'secondary'}>
                                            {prop.status || 'Active'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Button variant="ghost" size="icon" asChild title="View on Site">
                                                <a href={`/property/${prop.id}`} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            </Button>
                                            {!prop.rera_verified && prop.rera_number && (
                                                <Button
                                                    size="sm"
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                    onClick={() => handleApproveRera(prop.id)}
                                                    title="Approve RERA"
                                                >
                                                    <Award className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleToggleVerify(prop.id)}
                                                className={`h-8 px-2 ${prop.is_verified ? 'text-amber-600' : 'text-sky-600'}`}
                                                title={prop.is_verified ? 'Unverify Property' : 'Verify Property'}
                                            >
                                                <Shield className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleToggleSponsor(prop.id)}
                                                className={`h-8 px-2 ${prop.is_sponsored ? 'text-gray-400' : 'text-amber-500'}`}
                                                title={prop.is_sponsored ? 'Remove Sponsorship' : 'Mark as Sponsored'}
                                            >
                                                <Star className={`w-4 h-4 ${prop.is_sponsored ? 'fill-amber-500 text-amber-500' : ''}`} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
